import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from '@/components/forge/Chip'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { HierarchyPicker } from '@/components/forge/HierarchyPicker'
import { PlaceFilter } from '@/components/forge/PlaceFilter'
import { Icon } from '@/components/forge/Icon'
import { Eyebrow, Num } from '@/components/forge/primitives'
import { ATTACH_KINDS, LEVEL_RANK, rankOf } from '@/features/documents/api'
import { ROOT_ID } from '@/features/hierarchy/api'
import { useAssetClasses } from '@/features/assetClasses/queries'
import { useSensors } from '@/features/sensors/queries'
import { MAX_PAGE_SIZE } from '@/features/sensors/api'
import { usePermission } from '@/features/auth/permissions'

/**
 * What a document is attached to.
 *
 * ── Five kinds, and a document may carry a MIX ───────────────────────────
 * `DocumentAttachment` has five nullable FKs and the payload has five id lists.
 * A compressor manual can be attached to two machines AND the asset class they
 * share AND the vibration sensor the procedure is about, all at once. The demo
 * could not express that: its wizard asked for a scope first and then offered
 * one tier's targets, so every document it filed was attached to one kind of
 * thing.
 *
 * So the kind select here narrows WHAT YOU ARE BROWSING, not what the document
 * may hold. The chip row above it is the whole selection, across every kind,
 * and is what the form sends.
 *
 * ── The owner plant and the level fence what is offered ──────────────────
 * `verify_attachment_changes` refuses a target outside the owner plant (when
 * there is one) and a target ranked below the document's level. So a
 * plant-owned document's places are browsed from that plant down, never from
 * the account; a `line` document offers no machines; and asset classes are
 * filtered to the ones the server would take. Sensors are listed by machine,
 * so they are offered only to a `machine` document.
 *
 * ── Two attach keys, and two kinds need a permission beyond them ─────────
 * Places and sensors need `documents.attach_assets` (or manage); asset classes
 * `documents.attach_classes` (or manage). On top of that, asset classes come
 * from `/assets/classes` and sensors from `/sensors`, behind
 * `assets.manage_classes` and `sensors.manage_sensors`. Where a kind cannot be
 * attached or its list cannot be read, it is left out of the select and the
 * reason is stated — rather than offering an empty picker, which reads as
 * "there are none".
 *
 * ── Places are chosen in the shared hierarchy dialog ─────────────────────
 * Plant, line and machine open `HierarchyPicker` — the dialog Settings → Users,
 * the anomaly list and every place field use — in its multi-select mode, limited
 * to the kind being browsed. It replaced chip lists fed by the whole-estate
 * `useHierarchyNodes`, which drew every machine in the account as a chip.
 *
 * ── Sensors need a machine first ─────────────────────────────────────────
 * `list_sensors` will answer unfiltered, and an account's whole sensor estate
 * is thousands of rows that nobody scrolls. A machine select narrows it to the
 * handful somebody is actually thinking about, which is also how the logbook's
 * tag picker works.
 */
export function DocumentAttachmentPicker({
  attachments,
  onChange,
  disabled,
  ownerPlant = null,
  level = 'machine',
  canAttachAssets = true,
  canAttachClasses = true,
}) {
  const { t } = useTranslation(['app', 'common'])
  const levelRank = LEVEL_RANK[level] ?? LEVEL_RANK.machine

  const [browsed, setKind] = useState('plant')
  /* The narrowing machine, as `PlaceFilter` speaks it — `{id, tier, name, trail}`. */
  const [sensorMachine, setSensorMachine] = useState(null)
  const sensorMachineId = sensorMachine?.id ?? ''
  const [browsingPlaces, setBrowsingPlaces] = useState(false)

  const canReadClasses = usePermission('assets.manage_classes').granted
  const canReadSensors = usePermission('sensors.manage_sensors').granted

  /* The kinds this reader may attach AND can browse, at or above the level.
     Plant, line and machine come off the hierarchy's own paged endpoints. */
  const kinds = ATTACH_KINDS.filter((entry) => {
    if (entry === 'asset_class') return canAttachClasses && canReadClasses
    if (!canAttachAssets) return false
    if (entry === 'sensor') return canReadSensors && levelRank >= LEVEL_RANK.machine
    return LEVEL_RANK[entry] <= levelRank
  })
  /* The browsed kind, held to what is on offer when the level or a grant takes
     it away — derived rather than reset in an effect. */
  const kind = kinds.includes(browsed) ? browsed : (kinds[0] ?? null)

  const classes = useAssetClasses({ enabled: canReadClasses })
  const sensors = useSensors({
    machineId: sensorMachineId,
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    enabled: canReadSensors && kind === 'sensor' && Boolean(sensorMachineId),
  })

  const placeKind = Boolean(PLACE_TIER[kind])

  /** The options for whichever non-place kind is being browsed. A few dozen rows
      at most, so recomputed rather than memoised. */
  const options = (() => {
    if (kind === 'asset_class')
      return (classes.data?.items ?? [])
        .filter((entry) => {
          const rank = rankOf('asset_class', entry.entityType)
          /* A plant-owned document takes only that plant's classes — an
             account-wide class has a null plant and is refused too. */
          return (
            (rank === null || rank <= levelRank) && (!ownerPlant || entry.plantId === ownerPlant.id)
          )
        })
        .map((entry) => ({
          id: entry.id,
          label: entry.name,
          rank: rankOf('asset_class', entry.entityType),
        }))
    if (kind === 'sensor')
      return (sensors.data?.items ?? []).map((sensor) => ({
        id: sensor.id,
        label: sensor.name,
        rank: rankOf('sensor'),
      }))
    return []
  })()

  const held = (id) => attachments.some((one) => one.id === id)

  const toggle = (option) =>
    onChange((current) =>
      current.some((one) => one.id === option.id)
        ? current.filter((one) => one.id !== option.id)
        : /* The NAME is carried beside the id, and it is this client's copy for
             rendering only. The server re-resolves every name on the read from
             the entity itself, so a machine renamed after this dialog opened
             shows its old name on the chip and its new one the moment the
             document is saved and read back. */
          /* `rank` travels with it so a later change of level can drop what
             the new level would refuse, without re-reading the class. */
          [...current, { kind, id: option.id, name: option.label, rank: option.rank }],
    )

  const remove = (id) => onChange((current) => current.filter((one) => one.id !== id))

  const listPending =
    (kind === 'asset_class' && classes.isPending) ||
    (kind === 'sensor' && Boolean(sensorMachineId) && sensors.isPending)

  /* The attachments of the kind being browsed, as the picker's own nodes. */
  const heldPlaces = placeKind
    ? attachments
        .filter((one) => one.kind === kind)
        .map((one) => ({ id: one.id, tier: PLACE_TIER[kind], name: one.name }))
    : []

  /* The picker returns the whole selection for this kind; the other kinds are
     left exactly as they were. */
  const setPlaces = (places) =>
    onChange((current) => [
      ...current.filter((one) => one.kind !== kind),
      ...places.map((place) => ({
        kind,
        id: place.id,
        name: place.name,
        rank: LEVEL_RANK[kind],
      })),
    ])

  /* A plant-owned document is browsed from its plant down: the plant is the
     picker's root, so nothing outside it can be reached, let alone chosen. */
  const plantRoot = ownerPlant ? { id: ownerPlant.id, tier: 'site', name: ownerPlant.name } : null
  const root = plantRoot
    ? { ...plantRoot, parentId: null, code: null, isActive: true }
    : {
        id: ROOT_ID,
        tier: 'enterprise',
        parentId: null,
        name: t('placeFilter.all'),
        code: null,
        isActive: true,
      }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Eyebrow className="mb-1.5">{t('dataFusion.docs.form.attachments')}</Eyebrow>
        <p className="m-0 max-w-[62ch] text-body-sm text-ink-dim">
          {t('dataFusion.docs.form.attachmentsHint')}
        </p>
        {/* The fence, said before the reader runs into it. */}
        <p className="mt-1.5 mb-0 max-w-[62ch] text-body-sm text-ink-dim">
          {ownerPlant
            ? t('dataFusion.docs.form.fencePlant', { plant: ownerPlant.name })
            : t('dataFusion.docs.form.fenceAccount')}{' '}
          {t(`dataFusion.docs.form.fenceLevel.${level}`)}
        </p>
      </div>

      {/* The whole selection, across every kind. Above the browser rather than
          below it, because it is the answer and the list below is the means. */}
      {attachments.length ? (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {attachments.map((one) => (
            <li key={one.id}>
              <ForgeButton
                type="button"
                size="sm"
                disabled={disabled}
                onClick={() => remove(one.id)}
                aria-label={t('dataFusion.docs.form.attachmentRemove', {
                  name: one.name,
                })}
              >
                <Icon name={KIND_ICON[one.kind]} size="xs" className="text-ink-mute" />
                {one.name}
                <Icon name="x" size="xs" />
              </ForgeButton>
            </li>
          ))}
        </ul>
      ) : (
        /* Stated rather than left blank: at least one attachment is a server
           rule, and `at_least_one_attachment` answers keyed `body`, with no
           field to hang an error on. */
        <p className="m-0 text-body-sm text-on-amber">
          {t('dataFusion.docs.form.attachmentsRequired')}
        </p>
      )}

      {kinds.length ? (
        <div className="flex flex-wrap items-end gap-2">
          <ForgeSelect
            size="sm"
            label={t('dataFusion.docs.form.attachKind')}
            value={kind}
            onValueChange={setKind}
            disabled={disabled}
            options={kinds.map((entry) => ({
              value: entry,
              label: t(`dataFusion.docs.attachKind.${entry}`),
            }))}
          />

          {kind === 'sensor' ? (
            <PlaceFilter
              id="document-sensor-machine"
              value={sensorMachine}
              onChange={setSensorMachine}
              allowTiers={MACHINE_ONLY}
              disabled={disabled}
              label={t('dataFusion.docs.form.sensorMachine')}
              emptyLabel={t('dataFusion.docs.form.sensorMachinePlaceholder')}
              title={t('dataFusion.docs.form.sensorMachine')}
              note=""
              within={plantRoot}
            />
          ) : null}

          {placeKind ? (
            <ForgeButton size="sm" disabled={disabled} onClick={() => setBrowsingPlaces(true)}>
              <Icon name={KIND_ICON[kind]} size="xs" />
              {t(`dataFusion.docs.form.choosePlaces.${kind}`)}
            </ForgeButton>
          ) : null}
        </div>
      ) : (
        <p className="m-0 text-body-sm text-on-amber">
          {t('dataFusion.docs.form.nothingToAttach')}
        </p>
      )}

      {/* The two kinds that need a permission this form does not imply. Said,
          not drawn as an empty list. */}
      {!canReadClasses || !canReadSensors ? (
        <p className="m-0 text-body-sm text-ink-dim">
          {t('dataFusion.docs.form.attachKindsHidden')}
        </p>
      ) : null}

      {placeKind || !kind ? null : kind === 'sensor' && !sensorMachineId ? (
        <p className="m-0 text-body-sm text-ink-dim">
          {t('dataFusion.docs.form.sensorNeedMachine')}
        </p>
      ) : listPending ? (
        <p className="m-0 text-body-sm text-ink-dim">{t('common:loading')}</p>
      ) : options.length === 0 ? (
        <p className="m-0 text-body-sm text-ink-dim">
          {t(`dataFusion.docs.form.noneOfKind.${kind}`)}
        </p>
      ) : (
        <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto rounded-sm border border-hairline bg-sunken p-2.5">
          {options.map((option) => (
            <Chip
              key={option.id}
              selected={held(option.id)}
              disabled={disabled}
              onClick={() => toggle(option)}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      )}

      {attachments.length ? (
        <p className="m-0 text-body-sm text-ink-dim">
          <Num>
            {t('dataFusion.docs.form.attachmentCount', {
              count: attachments.length,
            })}
          </Num>
        </p>
      ) : null}

      {browsingPlaces && placeKind ? (
        <HierarchyPicker
          open
          onClose={() => setBrowsingPlaces(false)}
          title={t(`dataFusion.docs.form.choosePlaces.${kind}`)}
          root={root}
          value={heldPlaces}
          onChange={setPlaces}
          allowTiers={new Set([PLACE_TIER[kind]])}
          note={t('dataFusion.docs.form.choosePlacesNote')}
        />
      ) : null}
    </div>
  )
}

/** The attach kinds that are places, and the hierarchy tier each one is. */
const PLACE_TIER = { plant: 'site', line: 'line', machine: 'asset' }

const MACHINE_ONLY = new Set(['asset'])

/**
 * One glyph per kind, so a chip row of five different things stays readable.
 *
 * `plant` is a SITE in this product's vocabulary and `machine` is an ASSET —
 * the icons follow the product, not the wire. See `features/hierarchy/api.js`.
 */
export const KIND_ICON = {
  plant: 'map',
  line: 'gauge',
  machine: 'factory',
  asset_class: 'layers',
  sensor: 'wave',
}
